import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail, Phone, Globe } from 'lucide-react';
import logo from '../logo.jpg';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
              <img src={logo} alt="SupaKid Logo" className="w-8 h-8" />
              <span className="text-xl font-bold text-gray-800">SupaKid</span>
            </Link>
            <Link 
              to="/" 
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-8 py-12 text-white">
            <div className="flex items-center space-x-3 mb-4">
              <Shield className="w-8 h-8" />
              <h1 className="text-3xl font-bold">Privacy Policy</h1>
            </div>
            <p className="text-blue-100 text-lg">
              Your privacy and your child's safety are our top priorities
            </p>
            <p className="text-blue-200 text-sm mt-2">
              Last Updated: January 2026
            </p>
          </div>

          {/* Content */}
          <div className="px-8 py-8">
            <div className="prose prose-lg max-w-none">
              
              {/* Introduction */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">1. Introduction</h2>
                <p className="text-gray-600 leading-relaxed">
                  Supakid LLC ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the Supakid mobile application (the "App"). Please read this Privacy Policy carefully. By using the App, you agree to the collection and use of information in accordance with this policy.
                </p>
              </section>

              {/* Information We Collect */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">2. Information We Collect</h2>
                
                <h3 className="text-xl font-semibold text-gray-700 mb-3">2.1 Account Information</h3>
                <p className="text-gray-600 leading-relaxed mb-3">When you create an account, we collect:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li><strong>Email Address:</strong> Used for account authentication and communication</li>
                  <li><strong>Password:</strong> Encrypted and stored securely (we cannot see your password)</li>
                  <li><strong>Name:</strong> Your first and last name for account identification</li>
                  <li><strong>Admin PIN:</strong> A 4-digit PIN you create for accessing parental settings (stored securely)</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">2.2 Profile Information</h3>
                <p className="text-gray-600 leading-relaxed mb-3">When you create child profiles, we collect:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li><strong>Child's Name:</strong> The name you assign to each profile</li>
                  <li><strong>Age Group:</strong> Selected age category (Toddler 2-4, Preschool 5-7, School Age 8-12)</li>
                  <li><strong>Profile PIN:</strong> Optional 5-digit PIN for profile protection (if you choose to set one)</li>
                  <li><strong>Color Preference:</strong> Visual theme preference for each profile</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">2.3 Subscription Information</h3>
                <p className="text-gray-600 leading-relaxed mb-3">When you purchase a subscription, we collect:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li><strong>Subscription Status:</strong> Whether you have an active premium subscription</li>
                  <li><strong>Subscription Type:</strong> Monthly or Annual plan</li>
                  <li><strong>Subscription Expiration Date:</strong> When your subscription expires</li>
                  <li><strong>Payment Information:</strong> Processed through Apple's App Store (we do not store payment details)</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">2.4 Usage Information</h3>
                <p className="text-gray-600 leading-relaxed mb-3">We may collect:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li><strong>App Usage Data:</strong> How you interact with the App (features used, screens viewed)</li>
                  <li><strong>Screen Time Settings:</strong> Time limits you set for child profiles</li>
                  <li><strong>Device Information:</strong> Device type, operating system version, app version</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">2.5 Technical Information</h3>
                <p className="text-gray-600 leading-relaxed mb-3">We automatically collect:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li><strong>Log Data:</strong> Technical logs for debugging and improving the App</li>
                  <li><strong>Error Reports:</strong> Information about crashes or errors</li>
                  <li><strong>Analytics Data:</strong> Aggregated, anonymized usage statistics</li>
                </ul>
              </section>

              {/* How We Use Information */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">3. How We Use Your Information</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We use the information we collect to:
                </p>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">3.1 Provide and Improve the Service</h3>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li>Authenticate your account and provide access to the App</li>
                  <li>Manage your child profiles and preferences</li>
                  <li>Process subscription payments and manage premium access</li>
                  <li>Provide customer support</li>
                  <li>Improve the App's functionality and user experience</li>
                  <li>Fix bugs and technical issues</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">3.2 Communication</h3>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li>Send email verification codes during registration</li>
                  <li>Send important service updates and notifications</li>
                  <li>Respond to your support requests</li>
                  <li>Send subscription-related communications</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">3.3 Legal Compliance</h3>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Comply with applicable laws and regulations</li>
                  <li>Respond to legal requests or court orders</li>
                  <li>Protect our rights and prevent fraud</li>
                </ul>
              </section>

              {/* How We Store Information */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">4. How We Store Your Information</h2>
                
                <h3 className="text-xl font-semibold text-gray-700 mb-3">4.1 Data Storage</h3>
                <p className="text-gray-600 leading-relaxed mb-3">Your information is stored securely using:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li><strong>Supabase:</strong> A secure cloud database service (PostgreSQL)</li>
                  <li><strong>Encryption:</strong> All data is encrypted in transit (HTTPS/TLS)</li>
                  <li><strong>Secure Storage:</strong> Passwords and PINs are hashed and encrypted</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">4.2 Data Location</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Your data is stored on secure servers. The exact location depends on Supabase's infrastructure, which may be located in the United States or other countries.
                </p>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">4.3 Data Retention</h3>
                <p className="text-gray-600 leading-relaxed mb-3">We retain your information for as long as:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Your account is active</li>
                  <li>Necessary to provide the service</li>
                  <li>Required by law or legal obligations</li>
                </ul>
                <p className="text-gray-600 leading-relaxed mt-3">
                  When you delete your account, we permanently delete all associated data within 30 days.
                </p>
              </section>

              {/* Information Sharing */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">5. Information Sharing and Disclosure</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
                </p>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">5.1 Service Providers</h3>
                <p className="text-gray-600 leading-relaxed mb-3">We use third-party services that require access to your information:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li><strong>Supabase:</strong> Database and authentication services</li>
                  <li><strong>RevenueCat:</strong> Subscription management services</li>
                  <li><strong>Apple App Store:</strong> Payment processing and subscription management</li>
                  <li><strong>YouTube API:</strong> Video content delivery (subject to YouTube's privacy policy)</li>
                </ul>
                <p className="text-gray-600 leading-relaxed mb-6">
                  These service providers are contractually obligated to protect your information and use it only for the purposes we specify.
                </p>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">5.2 Legal Requirements</h3>
                <p className="text-gray-600 leading-relaxed mb-3">We may disclose your information if required by:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li>Law, regulation, or legal process</li>
                  <li>Government requests or court orders</li>
                  <li>Protection of our rights, property, or safety</li>
                  <li>Prevention of fraud or illegal activity</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">5.3 Business Transfers</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any such change.
                </p>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">5.4 With Your Consent</h3>
                <p className="text-gray-600 leading-relaxed">
                  We may share your information with your explicit consent or at your direction.
                </p>
              </section>

              {/* Third-Party Services */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">6. Third-Party Services</h2>
                
                <h3 className="text-xl font-semibold text-gray-700 mb-3">6.1 YouTube</h3>
                <p className="text-gray-600 leading-relaxed mb-3">The App uses YouTube's API to provide video content. Your use of YouTube content is subject to:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li><strong>YouTube Terms of Service:</strong> <a href="https://www.youtube.com/static?template=terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">https://www.youtube.com/static?template=terms</a></li>
                  <li><strong>YouTube Privacy Policy:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">https://policies.google.com/privacy</a></li>
                  <li><strong>Google Privacy Policy:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">https://policies.google.com/privacy</a></li>
                </ul>
                <p className="text-gray-600 leading-relaxed mb-6">
                  We do not control YouTube's data collection practices. Please review YouTube's privacy policy for information about how they handle your data.
                </p>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">6.2 Apple App Store</h3>
                <p className="text-gray-600 leading-relaxed mb-3">When you purchase a subscription, Apple processes your payment. Apple's collection and use of your information is governed by:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li><strong>Apple Privacy Policy:</strong> <a href="https://www.apple.com/privacy/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">https://www.apple.com/privacy/privacy-policy/</a></li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">6.3 RevenueCat</h3>
                <p className="text-gray-600 leading-relaxed mb-3">We use RevenueCat to manage subscriptions. RevenueCat's privacy practices are governed by:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li><strong>RevenueCat Privacy Policy:</strong> <a href="https://www.revenuecat.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">https://www.revenuecat.com/privacy</a></li>
                </ul>
              </section>

              {/* Children's Privacy */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">7. Children's Privacy</h2>
                
                <h3 className="text-xl font-semibold text-gray-700 mb-3">7.1 COPPA Compliance</h3>
                <p className="text-gray-600 leading-relaxed mb-3">Supakid is designed for families and complies with the Children's Online Privacy Protection Act (COPPA). We:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li>Do not knowingly collect personal information directly from children under 13</li>
                  <li>Require parent/guardian consent for account creation</li>
                  <li>Allow parents to create and manage child profiles</li>
                  <li>Provide parents with full control over their children's data</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">7.2 Parental Control</h3>
                <p className="text-gray-600 leading-relaxed mb-3">Parents have full control over:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li>Creating and deleting child profiles</li>
                  <li>Setting screen time limits</li>
                  <li>Managing profile PINs</li>
                  <li>Accessing and deleting all account data</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">7.3 Child Profile Data</h3>
                <p className="text-gray-600 leading-relaxed mb-3">Child profiles contain only:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Names assigned by parents</li>
                  <li>Age group selections</li>
                  <li>Optional PINs (if set by parents)</li>
                  <li>Screen time preferences</li>
                </ul>
                <p className="text-gray-600 leading-relaxed mt-3">
                  We do not collect personal information directly from children.
                </p>
              </section>

              {/* Your Rights */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">8. Your Rights and Choices</h2>
                
                <h3 className="text-xl font-semibold text-gray-700 mb-3">8.1 Access and Update</h3>
                <p className="text-gray-600 leading-relaxed mb-3">You can access and update your information at any time through the App's settings:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li>Update your name</li>
                  <li>Change your admin PIN</li>
                  <li>Modify child profiles</li>
                  <li>Update screen time settings</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">8.2 Account Deletion</h3>
                <p className="text-gray-600 leading-relaxed mb-3">You can delete your account at any time:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li>Go to Settings → Account Settings → Delete Account</li>
                  <li>All your data will be permanently deleted within 30 days</li>
                  <li>This action cannot be undone</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">8.3 Subscription Management</h3>
                <p className="text-gray-600 leading-relaxed mb-3">You can manage your subscription through:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li>Apple ID account settings</li>
                  <li>The App's subscription screen</li>
                  <li>Contacting Apple Support</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">8.4 Email Preferences</h3>
                <p className="text-gray-600 leading-relaxed mb-3">You can opt out of marketing emails by:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li>Clicking "unsubscribe" in any email</li>
                  <li>Contacting us at support@supakidapp.com</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">8.5 Data Portability</h3>
                <p className="text-gray-600 leading-relaxed">
                  You can request a copy of your data by contacting us at support@supakidapp.com.
                </p>
              </section>

              {/* Security Measures */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">9. Security Measures</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We implement industry-standard security measures to protect your information:
                </p>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">9.1 Technical Safeguards</h3>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li><strong>Encryption:</strong> All data transmitted using HTTPS/TLS</li>
                  <li><strong>Secure Storage:</strong> Passwords and PINs are hashed using secure algorithms</li>
                  <li><strong>Access Controls:</strong> Limited access to data on a need-to-know basis</li>
                  <li><strong>Regular Updates:</strong> Security patches and updates applied regularly</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">9.2 Organizational Safeguards</h3>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
                  <li><strong>Employee Training:</strong> Staff trained on data protection</li>
                  <li><strong>Access Restrictions:</strong> Limited employee access to user data</li>
                  <li><strong>Incident Response:</strong> Procedures for security incidents</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-700 mb-3">9.3 Your Responsibility</h3>
                <p className="text-gray-600 leading-relaxed mb-3">You are responsible for:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Keeping your account credentials secure</li>
                  <li>Not sharing your password or admin PIN</li>
                  <li>Logging out when using shared devices</li>
                  <li>Notifying us immediately of any security concerns</li>
                </ul>
              </section>

              {/* International Users */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">10. International Users</h2>
                <p className="text-gray-600 leading-relaxed">
                  If you are using the App from outside the United States, please note that:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mt-3">
                  <li>Your information may be transferred to and stored in the United States</li>
                  <li>Data protection laws may differ from those in your country</li>
                  <li>By using the App, you consent to the transfer of your information to the United States</li>
                </ul>
              </section>

              {/* California Privacy Rights */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">11. California Privacy Rights</h2>
                <p className="text-gray-600 leading-relaxed mb-3">
                  If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
                  <li>Right to know what personal information we collect</li>
                  <li>Right to delete your personal information</li>
                  <li>Right to opt out of the sale of personal information (we do not sell your information)</li>
                  <li>Right to non-discrimination for exercising your privacy rights</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  To exercise these rights, contact us at <a href="mailto:support@supakidapp.com" className="text-blue-600 hover:text-blue-800">support@supakidapp.com</a>.
                </p>
              </section>

              {/* Changes to Privacy Policy */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">12. Changes to This Privacy Policy</h2>
                <p className="text-gray-600 leading-relaxed mb-3">
                  We may update this Privacy Policy from time to time. We will notify you of material changes by:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-3">
                  <li>Posting the updated Privacy Policy in the App</li>
                  <li>Sending an email to your registered email address</li>
                  <li>Displaying a notice in the App</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  The "Last Updated" date at the top indicates when this policy was last revised. Your continued use of the App after changes become effective constitutes acceptance of the updated Privacy Policy.
                </p>
              </section>

              {/* Contact Information */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">13. Contact Us</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="space-y-4">
                    <div>
                      <p className="font-semibold text-gray-800 mb-2">Supakid LLC</p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Mail className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="font-semibold text-gray-800">Email</p>
                        <a href="mailto:support@supakidapp.com" className="text-blue-600 hover:text-blue-800">
                          support@supakidapp.com
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Phone className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="font-semibold text-gray-800">Phone</p>
                        <a href="tel:+17653511136" className="text-blue-600 hover:text-blue-800">
                          (765) 351-1136
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Globe className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="font-semibold text-gray-800">Website</p>
                        <a href="https://www.supakidapp.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                          https://www.supakidapp.com
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Acknowledgment */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">14. Acknowledgment</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  By using the App, you acknowledge that you have read and understood this Privacy Policy and agree to the collection and use of your information as described herein.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
                  <p className="text-blue-800 font-semibold text-center">
                    Your privacy is important to us. We are committed to protecting your personal information and being transparent about our practices.
                  </p>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-50 mt-12 border-t border-gray-200">
        <div className="container mx-auto px-4 py-6 text-center">
          <div className="flex items-center justify-center space-x-2 mb-3">
            <img src={logo} alt="SupaKid Logo" className="w-6 h-6" />
            <span className="text-lg font-bold text-gray-800">SupaKid</span>
          </div>
          <p className="text-sm text-gray-600 mb-2">
            Questions? Contact us at{" "}
            <a
              href="mailto:support@supakidapp.com"
              className="text-blue-600 hover:text-blue-800"
            >
              support@supakidapp.com
            </a>
          </p>
          <p className="text-xs text-gray-500 mb-3">© 2024 SupaKid. All rights reserved.</p>
          <div className="flex items-center justify-center space-x-4">
            <Link 
              to="/terms-of-service" 
              className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
